import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronRight, Search, X, Mail, AlertTriangle, Download } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/utils";
import { AllContactsTab } from "@/components/customers/all-contacts-tab";
import { AddressesTab } from "@/components/customers/addresses-tab";
import { CommunicationHistoryTab } from "@/components/customers/communication-history-tab";
import { CustomerMetricCards } from "@/components/customers/customer-metric-cards";
import { CollectionsTab } from "@/components/customers/collections-tab";
import { PromisesTab } from "@/components/customers/promises-tab";
import { DisputesTab } from "@/components/customers/disputes-tab";
import { AgeingBarChart } from "@/components/customers/ageing-bar-chart";
import { NotesTab } from "@/components/customers/notes-tab";
import { DocumentsTab } from "@/components/customers/documents-tab";
import { UserAvatar } from "@/components/user-avatar";
import { mockUsers } from "@/lib/mock-users";
import { useCustomerDetails, useCustomerInvoices, useCustomerPayments } from "@/hooks/useCustomerDetailData";
import { useCustomerTransactions, useCustomerCompletedTransactions } from "@/hooks/useTransactionsData";
import type { CustomerDetailUI } from "@/types/ui/customer-detail";
import type { Transaction } from "@/types/transaction";
import { useApi } from "@/hooks/useApi";
import { useMode } from "@/providers/ModeProvider";
import { TransactionTable } from "@/components/transactions/transaction-table";
import { TransactionDetailSheet } from "@/components/transactions/transaction-detail-sheet";
import { SelectionToolbar } from "@/components/transactions/selection-toolbar";
import { SendRemindersDialog } from "@/components/transactions/send-reminders-dialog";
import { AllocateReceiptDialog } from "@/components/transactions/allocate-receipt-dialog";
import { ColumnConfigDialog } from "@/components/transactions/column-config-dialog";
import { SendCopyInvoiceDialog } from "@/components/transactions/send-copy-invoice-dialog";
import { 
  CreateDisputeModal,
  AddNoteModal,
  type DisputeFormData,
  type NoteFormData,
} from "@/components/transactions/transaction-action-modals";
import { CreateCaseDrawer } from "@/components/collections/create-case-drawer";
import { CreatePromiseDrawer, type PromiseFormData } from "@/components/collections/create-promise-drawer";
import { exportTransactionsToExcel, calculateTransactionTotals } from "@/lib/export-utils";
import { useColumnConfig } from "@/hooks/useColumnConfig";

// Customer type alias available for future use
void (null as unknown as CustomerDetailUI);

// Types
interface Invoice {
  id: string;
  date: string;
  dueDate: string;
  amount: number;
  status: "overdue" | "outstanding" | "paid";
}

interface Payment {
  id: string;
  date: string;
  amount: number;
  method: string;
  reference: string;
  allocations: {
    invoiceId: string;
    invoiceDate: string;
    amount: number;
  }[];
}

// Mock Invoices (kept for potential future use)
const mockInvoices: Record<string, Invoice[]> = {
  "1": [
    {
      id: "INV-001",
      date: "2023-11-01",
      dueDate: "2023-12-01",
      amount: 76000,
      status: "overdue",
    },
    {
      id: "INV-002",
      date: "2023-11-15",
      dueDate: "2023-12-15",
      amount: 68000,
      status: "overdue",
    },
    {
      id: "INV-003",
      date: "2023-12-01",
      dueDate: "2023-12-31",
      amount: 70000,
      status: "overdue",
    },
  ],
};

// Mock Payments (kept for potential future use)
const mockPayments: Record<string, Payment[]> = {
  "1": [
    {
      id: "PAY-001",
      date: "2023-11-15",
      amount: 50000,
      method: "Wire Transfer",
      reference: "WT-2023-001",
      allocations: [
        { invoiceId: "INV-001", invoiceDate: "2023-10-01", amount: 30000 },
        { invoiceId: "INV-002", invoiceDate: "2023-10-15", amount: 20000 },
      ],
    },
    {
      id: "PAY-002",
      date: "2023-10-20",
      amount: 35000,
      method: "ACH",
      reference: "ACH-2023-002",
      allocations: [{ invoiceId: "INV-003", invoiceDate: "2023-09-01", amount: 35000 }],
    },
  ],
};

// Collection Strategies
const collectionStrategies = [
  { id: "1", name: "Standard Collection Flow", status: "active" },
  { id: "2", name: "Intensive Collection", status: "active" },
  { id: "3", name: "Gentle Reminder Flow", status: "inactive" },
];

// Mark mock data as intentionally kept for future use
void mockInvoices;
void mockPayments;

export function CustomerDetailPage() {
  const navigate = useNavigate();
  const { customerId } = useParams<{ customerId: string }>();
  const { apiPut } = useApi();
  const { mode } = useMode();

    // Fetch customer data using mode-aware hooks
    const { data: customer, isLoading, error: customerError } = useCustomerDetails(customerId);
    const { data: invoices = [] } = useCustomerInvoices(customerId);
    const { data: payments = [] } = useCustomerPayments(customerId);
    const { transactions: outstandingTransactions, isLoading: transactionsLoading } = useCustomerTransactions(customerId);
  const { transactions: completedTransactions, isLoading: completedTransactionsLoading } = useCustomerCompletedTransactions(customerId);

  const [editedCollectionStrategyId, setEditedCollectionStrategyId] = useState("");
  const [editedCreditControllerId, setEditedCreditControllerId] = useState("");
  const [editedAccountManagerId, setEditedAccountManagerId] = useState("");
  const [onStopValue, setOnStopValue] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [activeTransactionTab, setActiveTransactionTab] = useState("outstanding");

  // Transaction table state for History tab
  const [historyCurrentPage, setHistoryCurrentPage] = useState(1);
  const [historyPageSize, setHistoryPageSize] = useState(25);

  // Transaction table state for Outstanding tab
  const [outstandingCurrentPage, setOutstandingCurrentPage] = useState(1);
  const [outstandingPageSize, setOutstandingPageSize] = useState(25);

  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);

  // Selection state for Outstanding tab
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [sendRemindersDialogOpen, setSendRemindersDialogOpen] = useState(false);
  const [allocateReceiptDialogOpen, setAllocateReceiptDialogOpen] = useState(false);
  const [sendCopyInvoiceDialogOpen, setSendCopyInvoiceDialogOpen] = useState(false);
  const [createCollectionCaseModalOpen, setCreateCollectionCaseModalOpen] = useState(false);
  const [createPromiseModalOpen, setCreatePromiseModalOpen] = useState(false);
  const [createDisputeModalOpen, setCreateDisputeModalOpen] = useState(false);
  const [addNoteModalOpen, setAddNoteModalOpen] = useState(false);

  // Selection state for History tab
  const [historySelectedRows, setHistorySelectedRows] = useState<Set<number>>(new Set());
  const [historySendRemindersDialogOpen, setHistorySendRemindersDialogOpen] = useState(false);
  const [historyAllocateReceiptDialogOpen, setHistoryAllocateReceiptDialogOpen] = useState(false);
  const [historySendCopyInvoiceDialogOpen, setHistorySendCopyInvoiceDialogOpen] = useState(false);
  const [historyCreateCollectionCaseModalOpen, setHistoryCreateCollectionCaseModalOpen] = useState(false);
  const [historyCreatePromiseModalOpen, setHistoryCreatePromiseModalOpen] = useState(false);
  const [historyCreateDisputeModalOpen, setHistoryCreateDisputeModalOpen] = useState(false);
  const [historyAddNoteModalOpen, setHistoryAddNoteModalOpen] = useState(false);

  // Payment expansion state
  const [expandedPayments, setExpandedPayments] = useState<string[]>([]);

  // Search state
  const [outstandingSearchQuery, setOutstandingSearchQuery] = useState("");
  const [historySearchQuery, setHistorySearchQuery] = useState("");

  // Column configuration with persistence - Outstanding tab
  const { columnVisibility, columnOrder, saveConfig } = useColumnConfig("customer-detail-outstanding-columns");

  // Column configuration with persistence - History tab
  const {
    columnVisibility: historyColumnVisibility,
    columnOrder: historyColumnOrder,
    saveConfig: saveHistoryConfig
  } = useColumnConfig("customer-detail-history-columns");

  // Update collection strategy when customer data loads
  useEffect(() => {
    try {
      if (customer) {
        setEditedCollectionStrategyId(customer.collectionStrategyId || "none");
        setEditedCreditControllerId(customer.creditControllerId || "none");
        setEditedAccountManagerId(customer.accountManagerId || "none");
        setOnStopValue(customer.onStop ?? false);
      }
    } catch (error) {
      console.error("Error setting collection strategy:", error);
    }
  }, [customer]);

  // Clear selection and search when switching tabs
  useEffect(() => {
    setSelectedRows(new Set());
    setHistorySelectedRows(new Set());
    setOutstandingSearchQuery("");
    setHistorySearchQuery("");
  }, [activeTransactionTab]);

  const error = customerError ? (customerError instanceof Error ? customerError.message : "Failed to load customer") : null;

  // Filter transactions based on search query
  const filteredOutstandingTransactions = useMemo(() => {
    if (!outstandingSearchQuery.trim()) {
      return outstandingTransactions;
    }

    const query = outstandingSearchQuery.toLowerCase();
    return outstandingTransactions.filter((transaction) => {
      return (
        transaction.referenceNumber?.toLowerCase().includes(query) ||
        transaction.invoiceNumber?.toLowerCase().includes(query) ||
        transaction.description?.toLowerCase().includes(query) ||
        transaction.transactionType?.toLowerCase().includes(query) ||
        transaction.status?.toLowerCase().includes(query) ||
        transaction.amount?.toString().includes(query) ||
        transaction.outstandingAmount?.toString().includes(query) ||
        transaction.customerReference?.toLowerCase().includes(query) ||
        transaction.orderNumber?.toLowerCase().includes(query) ||
        transaction.accountCode?.toLowerCase().includes(query)
      );
    });
  }, [outstandingTransactions, outstandingSearchQuery]);

  const filteredCompletedTransactions = useMemo(() => {
    if (!historySearchQuery.trim()) {
      return completedTransactions;
    }

    const query = historySearchQuery.toLowerCase();
    return completedTransactions.filter((transaction) => {
      return (
        transaction.referenceNumber?.toLowerCase().includes(query) ||
        transaction.invoiceNumber?.toLowerCase().includes(query) ||
        transaction.description?.toLowerCase().includes(query) ||
        transaction.transactionType?.toLowerCase().includes(query) ||
        transaction.status?.toLowerCase().includes(query) ||
        transaction.amount?.toString().includes(query) ||
        transaction.outstandingAmount?.toString().includes(query) ||
        transaction.customerReference?.toLowerCase().includes(query) ||
        transaction.orderNumber?.toLowerCase().includes(query) ||
        transaction.accountCode?.toLowerCase().includes(query)
      );
    });
  }, [completedTransactions, historySearchQuery]);

  // Selection handlers for Outstanding tab
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedRows(new Set(filteredOutstandingTransactions.map((t) => t.id)));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (id: number, checked: boolean) => {
    const newSelected = new Set(selectedRows);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedRows(newSelected);
  };

  // Get selected transactions for Outstanding tab
  const selectedTransactions = outstandingTransactions.filter((t) => selectedRows.has(t.id));

  // Calculate totals for selected transactions (Outstanding tab)
  const selectionTotals = calculateTransactionTotals(selectedTransactions);

  // Get selected transactions for History tab
  const historySelectedTransactions = completedTransactions.filter((t) => historySelectedRows.has(t.id));

  // Calculate totals for selected transactions (History tab)
  const historySelectionTotals = calculateTransactionTotals(historySelectedTransactions);

  // Action handlers
    const handleExportSelected = () => {
      try {
        exportTransactionsToExcel(selectedTransactions);
        toast.success(`Exported ${selectedTransactions.length} selected transactions`);
      } catch {
        toast.error("Failed to export selected transactions");
      }
    };

    const handleExportAllOutstanding = () => {
      try {
        const transactionsToExport = outstandingSearchQuery 
          ? filteredOutstandingTransactions 
          : outstandingTransactions;
        exportTransactionsToExcel(transactionsToExport, `outstanding-transactions-${customer?.name || 'customer'}-${new Date().toISOString().split('T')[0]}.csv`);
        toast.success(`Exported ${transactionsToExport.length} outstanding transactions`);
      } catch {
        toast.error("Failed to export outstanding transactions");
      }
    };

    const handleSendReminders = (transactionIds: number[]) => {
    // In a real app, this would call an API
    toast.success(`Payment reminders sent for ${transactionIds.length} invoice(s)`);
    setSelectedRows(new Set());
    setSendRemindersDialogOpen(false);
  };

  const handleAllocateReceipt = (receiptId: number, allocations: { invoiceId: number; amount: number }[]) => {
    // In a real app, this would call an API
    toast.success(`Receipt allocated to ${allocations.length} invoice(s)`);
    setSelectedRows(new Set());
    setAllocateReceiptDialogOpen(false);
  };

  // Handler for navigating to allocate screen when unallocated receipts are selected
  const handleNavigateToAllocate = () => {
    // Get only unallocated receipts from selection
    const unallocatedReceipts = selectedTransactions.filter(
      (t) => t.transactionType === "receipt" && 
             t.outstandingAmount !== undefined && 
             t.outstandingAmount > 0
    );

    if (unallocatedReceipts.length === 0) {
      toast.error("No unallocated receipts selected");
      return;
    }

    // If only one receipt is selected, navigate directly to its allocate page
    if (unallocatedReceipts.length === 1) {
      navigate(`/payments/allocate/${unallocatedReceipts[0].id}`);
    } else {
      // For multiple receipts, navigate to the first one
      // In a real app, you might want to show a selection dialog or navigate to a list view
      navigate(`/payments/allocate/${unallocatedReceipts[0].id}`);
      toast.info(`Navigating to allocate screen for receipt ${unallocatedReceipts[0].referenceNumber}`);
    }
  };

  const handleClearSelection = () => {
    setSelectedRows(new Set());
  };

  const handleSendCopyInvoice = (email: string, message: string, transactionIds: number[]) => {
    // In a real app, this would call an API
    toast.success(`Invoice copies sent to ${email} for ${transactionIds.length} invoice(s)`);
    setSelectedRows(new Set());
    setSendCopyInvoiceDialogOpen(false);
  };

  const handleCreateCollectionCase = () => {
    // Called when case is created successfully from the drawer
    setCreateCollectionCaseModalOpen(false);
    setSelectedRows(new Set()); // Clear selection
  };

  const handleCreatePromise = (data: PromiseFormData) => {
    // In a real app, this would call an API
    const invoiceCount = data.selectedInvoices.length;
    toast.success(`Promise to pay recorded for ${invoiceCount} transaction(s)`);
    setCreatePromiseModalOpen(false);
    setSelectedRows(new Set()); // Clear selection
  };

  const handleCreateDispute = (data: DisputeFormData) => {
    // In a real app, this would call an API
    toast.success(`Dispute created for ${data.transactionIds.length} transaction(s)`);
    setCreateDisputeModalOpen(false);
    setSelectedRows(new Set()); // Clear selection
  };

  const handleAddNote = (data: NoteFormData) => {
    // In a real app, this would call an API
    const noteCount = data.customerIds.length;
    toast.success(`Note${noteCount > 1 ? 's' : ''} added to ${noteCount} customer${noteCount > 1 ? 's' : ''}`);
    setAddNoteModalOpen(false);
    setSelectedRows(new Set()); // Clear selection
  };

  // Selection handlers for History tab
  const handleHistorySelectAll = (checked: boolean) => {
    if (checked) {
      setHistorySelectedRows(new Set(filteredCompletedTransactions.map((t) => t.id)));
    } else {
      setHistorySelectedRows(new Set());
    }
  };

  const handleHistorySelectRow = (id: number, checked: boolean) => {
    const newSelected = new Set(historySelectedRows);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setHistorySelectedRows(newSelected);
  };

  // Action handlers for History tab
    const handleHistoryExportSelected = () => {
      try {
        exportTransactionsToExcel(historySelectedTransactions);
        toast.success(`Exported ${historySelectedTransactions.length} selected transactions`);
      } catch {
        toast.error("Failed to export selected transactions");
      }
    };

    const handleExportAllHistory = () => {
      try {
        const transactionsToExport = historySearchQuery 
          ? filteredCompletedTransactions 
          : completedTransactions;
        exportTransactionsToExcel(transactionsToExport, `completed-transactions-${customer?.name || 'customer'}-${new Date().toISOString().split('T')[0]}.csv`);
        toast.success(`Exported ${transactionsToExport.length} completed transactions`);
      } catch {
        toast.error("Failed to export completed transactions");
      }
    };

    const handleHistorySendReminders = (transactionIds: number[]) => {
    // In a real app, this would call an API
    toast.success(`Payment reminders sent for ${transactionIds.length} invoice(s)`);
    setHistorySelectedRows(new Set());
    setHistorySendRemindersDialogOpen(false);
  };

  const handleHistoryAllocateReceipt = (receiptId: number, allocations: { invoiceId: number; amount: number }[]) => {
    // In a real app, this would call an API
    toast.success(`Receipt allocated to ${allocations.length} invoice(s)`);
    setHistorySelectedRows(new Set());
    setHistoryAllocateReceiptDialogOpen(false);
  };

  // Handler for navigating to allocate screen from history tab
  const handleHistoryNavigateToAllocate = () => {
    // Get only unallocated receipts from selection
    const unallocatedReceipts = historySelectedTransactions.filter(
      (t) => t.transactionType === "receipt" && 
             t.outstandingAmount !== undefined && 
             t.outstandingAmount > 0
    );

    if (unallocatedReceipts.length === 0) {
      toast.error("No unallocated receipts selected");
      return;
    }

    // If only one receipt is selected, navigate directly to its allocate page
    if (unallocatedReceipts.length === 1) {
      navigate(`/payments/allocate/${unallocatedReceipts[0].id}`);
    } else {
      // For multiple receipts, navigate to the first one
      // In a real app, you might want to show a selection dialog or navigate to a list view
      navigate(`/payments/allocate/${unallocatedReceipts[0].id}`);
      toast.info(`Navigating to allocate screen for receipt ${unallocatedReceipts[0].referenceNumber}`);
    }
  };

  const handleHistoryClearSelection = () => {
    setHistorySelectedRows(new Set());
  };

  const handleHistorySendCopyInvoice = (email: string, message: string, transactionIds: number[]) => {
    // In a real app, this would call an API
    toast.success(`Invoice copies sent to ${email} for ${transactionIds.length} invoice(s)`);
    setHistorySelectedRows(new Set());
    setHistorySendCopyInvoiceDialogOpen(false);
  };

  const handleHistoryCreateCollectionCase = () => {
    // Called when case is created successfully from the drawer
    setHistoryCreateCollectionCaseModalOpen(false);
    setHistorySelectedRows(new Set()); // Clear selection
  };

  const handleHistoryCreatePromise = (data: PromiseFormData) => {
    // In a real app, this would call an API
    const invoiceCount = data.selectedInvoices.length;
    toast.success(`Promise to pay recorded for ${invoiceCount} transaction(s)`);
    setHistoryCreatePromiseModalOpen(false);
    setHistorySelectedRows(new Set()); // Clear selection
  };

  const handleHistoryCreateDispute = (data: DisputeFormData) => {
    // In a real app, this would call an API
    toast.success(`Dispute created for ${data.transactionIds.length} transaction(s)`);
    setHistoryCreateDisputeModalOpen(false);
    setHistorySelectedRows(new Set()); // Clear selection
  };

  const handleHistoryAddNote = (data: NoteFormData) => {
    // In a real app, this would call an API
    const noteCount = data.customerIds.length;
    toast.success(`Note${noteCount > 1 ? 's' : ''} added to ${noteCount} customer${noteCount > 1 ? 's' : ''}`);
    setHistoryAddNoteModalOpen(false);
    setHistorySelectedRows(new Set()); // Clear selection
  };

  // Risk badge helper
  const getRiskBadge = (risk: string) => {
    switch (risk) {
      case "high":
        return <Badge variant="destructive">High Risk</Badge>;
      case "medium":
        return (
          <Badge className="bg-amber-500 text-white hover:bg-amber-600">Medium Risk</Badge>
        );
      case "low":
        return <Badge className="bg-green-600 text-white hover:bg-green-700">Low Risk</Badge>;
      default:
        return <Badge variant="secondary">Unknown Risk</Badge>;
    }
  };

  // Handle save collection settings
  const handleSaveCollectionSettings = () => {
    if (!customer) return;

    try {
      // Note: In a real implementation, this would call an API to update the customer
      // For now, just show success message
      toast.success("Collection settings updated successfully");
    } catch (error) {
      console.error("Error saving collection settings:", error);
      toast.error("Failed to save collection settings");
    }
  };

  // Handle On Stop toggle
  const handleOnStopChange = async (checked: boolean) => {
    if (!customer || !customerId) return;

    setOnStopValue(checked);

    try {
      if (mode === "live") {
        // In live mode, call the API to update the customer
        await apiPut(`/api/v1/customers/${customerId}`, {
          onStop: checked,
        });
        toast.success(`On Stop status updated to ${checked ? "Yes" : "No"}`);
      } else {
        // In demo mode, just show success message
        toast.success(`On Stop status updated to ${checked ? "Yes" : "No"}`);
      }
    } catch (error) {
      console.error("Error updating On Stop status:", error);
      toast.error("Failed to update On Stop status");
      // Revert the change on error
      setOnStopValue(!checked);
    }
  };

        // Calculate aging based on outstanding transactions
        const calculateAging = () => {
    const today = new Date();

    const aging = {
      current: 0,
      days1_30: 0,
      days31_60: 0,
      days61_90: 0,
      days90Plus: 0,
    };

    // Use outstanding transactions instead of invoices
    outstandingTransactions.forEach((transaction) => {
      // Only process transactions with outstanding amounts
      if (!transaction.outstandingAmount || transaction.outstandingAmount <= 0) return;
      
      // Skip if no due date
      if (!transaction.dueDate) return;

      const dueDate = new Date(transaction.dueDate);
      const daysOverdue = Math.floor(
        (today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24)
      );

      // Use outstanding amount instead of full amount
      const amount = transaction.outstandingAmount;

      // Categorize based on days overdue
      if (daysOverdue < 0) {
        // Not yet due (current/future)
        aging.current += amount;
      } else if (daysOverdue <= 30) {
        // 0-30 days overdue (current bucket in the chart)
        aging.current += amount;
      } else if (daysOverdue <= 60) {
        // 31-60 days overdue
        aging.days1_30 += amount;
      } else if (daysOverdue <= 90) {
        // 61-90 days overdue
        aging.days31_60 += amount;
      } else if (daysOverdue <= 120) {
        // 91-120 days overdue
        aging.days61_90 += amount;
      } else {
        // Over 120 days overdue
        aging.days90Plus += amount;
      }
    });

    return aging;
  };

          // Toggle payment expansion (kept for potential future use)
          const togglePaymentExpansion = (paymentId: string) => {
      if (expandedPayments.includes(paymentId)) {
        setExpandedPayments(expandedPayments.filter((id) => id !== paymentId));
      } else {
        setExpandedPayments([...expandedPayments, paymentId]);
      }
    };
    // Mark functions as intentionally kept for future use
    void togglePaymentExpansion;
    void calculateAging;

  console.log("Rendering CustomerDetailPage - customer state:", customer);
  console.log("isLoading:", isLoading, "error:", error);

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex flex-col gap-6 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-lg text-muted-foreground">Loading customer details...</p>
            <p className="text-sm text-muted-foreground mt-2">Customer ID: {customerId}</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="flex flex-col gap-6 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <h2 className="text-2xl font-semibold text-red-600 mb-2">Error Loading Customer</h2>
            <p className="text-muted-foreground mb-4">{error}</p>
            <p className="text-sm text-muted-foreground mb-4">Customer ID: {customerId}</p>
            <Button onClick={() => navigate("/customers")}>
              Back to Customers
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Show not found state
  if (!customer) {
    console.log("Customer is null - showing not found message");
    return (
      <div className="flex flex-col gap-6 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">Customer Not Found</h2>
            <p className="text-muted-foreground mb-2">
              The customer you're looking for doesn't exist or couldn't be loaded.
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              Customer ID: {customerId || "No ID provided"}
            </p>
            <Button onClick={() => navigate("/customers")}>
              Back to Customers
            </Button>
          </div>
        </div>
      </div>
    );
  }

  console.log("Customer found - rendering detail page for:", customer.name);

  // Main render - wrapped in try-catch for safety
  try {
    return (
      <div className="flex flex-col gap-6 p-6">
        {/* Breadcrumb Navigation */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <button
            onClick={() => navigate("/customers")}
            className="hover:text-foreground transition-colors"
          >
            Customers
          </button>
          <ChevronRight className="h-4 w-4" />
          <span className="text-foreground font-medium">{customer.name}</span>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold">{customer.name}</h1>
              {getRiskBadge(customer.risk)}
            </div>
            <p className="text-muted-foreground">{customer.accountNumber}</p>
          </div>
          <Button variant="outline" onClick={() => navigate("/customers")}>
            Back to Customers
          </Button>
        </div>

        {/* Metric Cards */}
        <CustomerMetricCards
          customer={customer}
          invoices={invoices}
          payments={payments.map((p) => ({
            id: p.id,
            date: p.date,
            amount: p.amount,
            invoiceDate: p.allocations[0]?.invoiceDate,
          }))}
          onCardClick={(action) => {
            switch (action) {
              case "outstanding":
                setActiveTab("invoices");
                setActiveTransactionTab("outstanding");
                break;
              case "overdue":
                setActiveTab("invoices");
                setActiveTransactionTab("outstanding");
                break;
              case "credit":
                setActiveTab("financial");
                break;
              case "performance":
                setActiveTab("invoices");
                setActiveTransactionTab("payment-history");
                break;
            }
          }}
        />

        {/* Customer Details Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-12">
            <TabsTrigger value="overview">
              Overview
            </TabsTrigger>
            <TabsTrigger value="financial">
              Financial
            </TabsTrigger>
            <TabsTrigger value="ageing">
              Ageing
            </TabsTrigger>
            <TabsTrigger value="invoices">
              Transactions
            </TabsTrigger>
            <TabsTrigger value="collections">
              Collections
            </TabsTrigger>
            <TabsTrigger value="promises">
              Promises
            </TabsTrigger>
            <TabsTrigger value="disputes">
              Disputes
            </TabsTrigger>
            <TabsTrigger value="communications">
              Communications
            </TabsTrigger>
            <TabsTrigger value="contacts">
              Contacts
            </TabsTrigger>
            <TabsTrigger value="addresses">
              Addresses
            </TabsTrigger>
            <TabsTrigger value="notes">
              Notes
            </TabsTrigger>
            <TabsTrigger value="documents">
              Documents
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>Account Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                    {/* Left Column - Existing Fields */}
                    <div className="space-y-2">
                      <div>
                        <Label className="text-muted-foreground">Account Number</Label>
                        <p className="font-medium">{customer.accountNumber}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Customer Name</Label>
                        <p className="font-medium">{customer.name}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Risk Level</Label>
                        <div className="mt-1">{getRiskBadge(customer.risk)}</div>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Currency</Label>
                        <p className="font-medium">{customer.currency}</p>
                      </div>
                    </div>

                    {/* Right Column - New ERP Fields */}
                    <div className="space-y-2">
                      <div>
                        <Label className="text-muted-foreground">On Stop</Label>
                        <div className="mt-1 flex items-center gap-2">
                          <Switch
                            checked={onStopValue}
                            onCheckedChange={handleOnStopChange}
                          />
                          <span className="text-sm font-medium">
                            {onStopValue ? "Yes" : "No"}
                          </span>
                        </div>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Status</Label>
                        <div className="mt-1">
                          {customer.status ? (
                            <Badge
                              className={
                                customer.status === "Active"
                                  ? "bg-green-600 text-white hover:bg-green-700"
                                  : "bg-gray-500 text-white hover:bg-gray-600"
                              }
                            >
                              {customer.status}
                            </Badge>
                          ) : (
                            <span className="text-sm text-muted-foreground">Not Available</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Contact Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div>
                    <Label className="text-muted-foreground">Email</Label>
                    <p className="font-medium">{customer.contact}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Phone</Label>
                    <p className="font-medium">{customer.phone}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Address</Label>
                    <p className="font-medium">{customer.address}</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Collection Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-end gap-4">
                  <div className="flex-1">
                    <Label>Assigned Strategy</Label>
                    <Select
                      value={editedCollectionStrategyId}
                      onValueChange={setEditedCollectionStrategyId}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select strategy" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {collectionStrategies
                          .filter((s) => s.status === "active")
                          .map((strategy) => (
                            <SelectItem key={strategy.id} value={strategy.id}>
                              {strategy.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <Label>Assigned Credit Controller</Label>
                    <Select
                      value={editedCreditControllerId}
                      onValueChange={setEditedCreditControllerId}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select user">
                          {editedCreditControllerId && editedCreditControllerId !== "none" && (
                            <UserAvatar
                              user={mockUsers.find((u) => u.id === editedCreditControllerId)!}
                              size="sm"
                              showName
                            />
                          )}
                          {editedCreditControllerId === "none" && "None"}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {mockUsers
                          .filter((u) => u.status === "active")
                          .map((user) => (
                            <SelectItem key={user.id} value={user.id}>
                              <UserAvatar user={user} size="sm" showName />
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <Label>Account Manager</Label>
                    <Select
                      value={editedAccountManagerId}
                      onValueChange={setEditedAccountManagerId}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select user">
                          {editedAccountManagerId && editedAccountManagerId !== "none" && (
                            <UserAvatar
                              user={mockUsers.find((u) => u.id === editedAccountManagerId)!}
                              size="sm"
                              showName
                            />
                          )}
                          {editedAccountManagerId === "none" && "None"}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {mockUsers
                          .filter((u) => u.status === "active")
                          .map((user) => (
                            <SelectItem key={user.id} value={user.id}>
                              <UserAvatar user={user} size="sm" showName />
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    onClick={handleSaveCollectionSettings}
                    disabled={
                      editedCollectionStrategyId === (customer.collectionStrategyId || "none") &&
                      editedCreditControllerId === (customer.creditControllerId || "none") &&
                      editedAccountManagerId === (customer.accountManagerId || "none")
                    }
                  >
                    Save
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Category Keys</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                  <div>
                    <Label className="text-muted-foreground">
                      {customer.userSortKey1Label || "User Sort Key 1"}
                    </Label>
                    <p className="font-medium">
                      {customer.userSortKey1 || <span className="text-sm text-muted-foreground">Not Available</span>}
                    </p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">
                      {customer.userSortKey2Label || "User Sort Key 2"}
                    </Label>
                    <p className="font-medium">
                      {customer.userSortKey2 || <span className="text-sm text-muted-foreground">Not Available</span>}
                    </p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">
                      {customer.userSortKey3Label || "User Sort Key 3"}
                    </Label>
                    <p className="font-medium">
                      {customer.userSortKey3 || <span className="text-sm text-muted-foreground">Not Available</span>}
                    </p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">
                      {customer.userSortKey4Label || "User Sort Key 4"}
                    </Label>
                    <p className="font-medium">
                      {customer.userSortKey4 || <span className="text-sm text-muted-foreground">Not Available</span>}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Financial Tab */}
          <TabsContent value="financial" className="space-y-4">
            <div className="grid gap-4 md:grid-cols-3">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Credit Limit</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold">
                    {formatCurrency(customer.creditLimit, customer.currency)}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Available Credit</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-green-600">
                    {formatCurrency(customer.creditLimit - customer.outstanding, customer.currency)}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Credit Utilisation</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>
                      {formatCurrency(customer.outstanding, customer.currency)} of{" "}
                      {formatCurrency(customer.creditLimit, customer.currency)}
                    </span>
                    <span className="font-medium">
                      {Math.round((customer.outstanding / customer.creditLimit) * 100)}%
                    </span>
                  </div>
                  <Progress value={(customer.outstanding / customer.creditLimit) * 100} />
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Payment Terms & Performance</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-3">
                  <div>
                    <Label className="text-muted-foreground">Payment Terms</Label>
                    <p className="text-lg font-medium">{customer.paymentTerms}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Due Days</Label>
                    <p className="text-lg font-medium">
                      {customer.dueDays !== undefined ? (
                        customer.dueDays
                      ) : (
                        <span className="text-sm text-muted-foreground">Not Available</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Due Days Type</Label>
                    <p className="text-lg font-medium">
                      {customer.dueDaysType || (
                        <span className="text-sm text-muted-foreground">Not Available</span>
                      )}
                    </p>
                  </div>
                </div>
                <div className="grid gap-4 md:grid-cols-3">
                  <div>
                    <Label className="text-muted-foreground">Payment Performance</Label>
                    <p className="text-lg font-medium">{customer.dso} days</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Risk Level</Label>
                    <div className="mt-1">{getRiskBadge(customer.risk)}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Ageing Tab */}
          <TabsContent value="ageing" className="space-y-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-base">Ageing Summary</CardTitle>
                {(() => {
                  const aging = calculateAging();
                  const overdueTotal = aging.days1_30 + aging.days31_60 + aging.days61_90 + aging.days90Plus;
                  return overdueTotal > 0 ? (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          // Navigate to transactions tab with outstanding filter
                          setActiveTab("invoices");
                          setActiveTransactionTab("outstanding");
                          // Open send reminders dialog
                          setTimeout(() => setSendRemindersDialogOpen(true), 100);
                        }}
                        className="h-8"
                      >
                        <Mail className="h-4 w-4 mr-2" />
                        Send Reminders
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          // Navigate to collections tab
                          setActiveTab("collections");
                          toast.info("Navigate to Collections tab to escalate high-risk accounts");
                        }}
                        className="h-8"
                      >
                        <AlertTriangle className="h-4 w-4 mr-2" />
                        Escalate to Collections
                      </Button>
                    </div>
                  ) : null;
                })()}
              </CardHeader>
              <CardContent className="pt-2 pb-3">
                <AgeingBarChart 
                  data={calculateAging()} 
                  onSendReminders={() => {
                    // Navigate to transactions tab with outstanding filter
                    setActiveTab("invoices");
                    setActiveTransactionTab("outstanding");
                    // Open send reminders dialog
                    setTimeout(() => setSendRemindersDialogOpen(true), 100);
                  }}
                  onEscalateToCollections={() => {
                    // Navigate to collections tab
                    setActiveTab("collections");
                    toast.info("Navigate to Collections tab to escalate high-risk accounts");
                  }}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Transactions Tab */}
          <TabsContent value="invoices" className="space-y-4">
            <Tabs value={activeTransactionTab} onValueChange={setActiveTransactionTab}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="outstanding">Outstanding</TabsTrigger>
                <TabsTrigger value="payment-history">History</TabsTrigger>
              </TabsList>

              {/* Outstanding Sub-Tab */}
              <TabsContent value="outstanding" className="space-y-4">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-base font-medium">
                      Outstanding Transactions
                      {outstandingSearchQuery && (
                        <span className="ml-2 text-sm font-normal text-muted-foreground">
                          ({filteredOutstandingTransactions.length} of {outstandingTransactions.length})
                        </span>
                      )}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                          type="text"
                          placeholder="Search transactions..."
                          value={outstandingSearchQuery}
                          onChange={(e) => setOutstandingSearchQuery(e.target.value)}
                          className="pl-8 pr-8 h-9 w-[250px]"
                        />
                        {outstandingSearchQuery && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setOutstandingSearchQuery("")}
                            className="absolute right-0 top-0 h-9 w-9 p-0 hover:bg-transparent"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleExportAllOutstanding}
                        disabled={filteredOutstandingTransactions.length === 0}
                        className="h-9"
                        title="Export to Excel"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      <ColumnConfigDialog
                        columnVisibility={columnVisibility}
                        columnOrder={columnOrder}
                        onApply={saveConfig}
                        iconOnly={true}
                      />
                    </div>
                  </CardHeader>
                  <CardContent className="pt-2">
                    {transactionsLoading ? (
                      <div className="flex items-center justify-center h-32">
                        <p className="text-muted-foreground">Loading transactions...</p>
                      </div>
                    ) : filteredOutstandingTransactions.length === 0 && outstandingSearchQuery ? (
                      <div className="flex flex-col items-center justify-center h-32 text-center">
                        <p className="text-muted-foreground">No transactions found matching "{outstandingSearchQuery}"</p>
                        <Button
                          variant="link"
                          onClick={() => setOutstandingSearchQuery("")}
                          className="mt-2"
                        >
                          Clear search
                        </Button>
                      </div>
                    ) : (
                      <TransactionTable
                        transactions={filteredOutstandingTransactions}
                        columnVisibility={columnVisibility}
                        onViewTransaction={(transaction) => {
                          setSelectedTransaction(transaction);
                          setDetailSheetOpen(true);
                        }}
                        showCustomerColumn={false}
                        enableSelection={true}
                        selectedRows={selectedRows}
                        onSelectRow={handleSelectRow}
                        onSelectAll={handleSelectAll}
                        currentPage={outstandingCurrentPage}
                        pageSize={outstandingPageSize}
                        onPageChange={setOutstandingCurrentPage}
                        onPageSizeChange={setOutstandingPageSize}
                      />
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Payment History Sub-Tab */}
              <TabsContent value="payment-history" className="space-y-4">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-base font-medium">
                      Transaction History
                      {historySearchQuery && (
                        <span className="ml-2 text-sm font-normal text-muted-foreground">
                          ({filteredCompletedTransactions.length} of {completedTransactions.length})
                        </span>
                      )}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                          type="text"
                          placeholder="Search transactions..."
                          value={historySearchQuery}
                          onChange={(e) => setHistorySearchQuery(e.target.value)}
                          className="pl-8 pr-8 h-9 w-[250px]"
                        />
                        {historySearchQuery && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setHistorySearchQuery("")}
                            className="absolute right-0 top-0 h-9 w-9 p-0 hover:bg-transparent"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleExportAllHistory}
                        disabled={filteredCompletedTransactions.length === 0}
                        className="h-9"
                        title="Export to Excel"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      <ColumnConfigDialog
                        columnVisibility={historyColumnVisibility}
                        columnOrder={historyColumnOrder}
                        onApply={saveHistoryConfig}
                        iconOnly={true}
                      />
                    </div>
                  </CardHeader>
                  <CardContent className="pt-2">
                    {completedTransactionsLoading ? (
                      <div className="flex items-center justify-center h-32">
                        <p className="text-muted-foreground">Loading completed transactions...</p>
                      </div>
                    ) : filteredCompletedTransactions.length === 0 && historySearchQuery ? (
                      <div className="flex flex-col items-center justify-center h-32 text-center">
                        <p className="text-muted-foreground">No transactions found matching "{historySearchQuery}"</p>
                        <Button
                          variant="link"
                          onClick={() => setHistorySearchQuery("")}
                          className="mt-2"
                        >
                          Clear search
                        </Button>
                      </div>
                    ) : (
                      <TransactionTable
                        transactions={filteredCompletedTransactions}
                        columnVisibility={historyColumnVisibility}
                        onViewTransaction={(transaction) => {
                          setSelectedTransaction(transaction);
                          setDetailSheetOpen(true);
                        }}
                        showCustomerColumn={false}
                        enableSelection={true}
                        selectedRows={historySelectedRows}
                        onSelectRow={handleHistorySelectRow}
                        onSelectAll={handleHistorySelectAll}
                        currentPage={historyCurrentPage}
                        pageSize={historyPageSize}
                        onPageChange={setHistoryCurrentPage}
                        onPageSizeChange={setHistoryPageSize}
                      />
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* Collections Tab */}
          <TabsContent value="collections">
            <CollectionsTab 
              customerId={customer.id} 
              customerName={customer.name}
              customerPhone={customer.phone}
              accountNumber={customer.accountNumber}
            />
          </TabsContent>

          {/* Promises Tab */}
          <TabsContent value="promises">
            <PromisesTab 
              customerId={customer.id} 
              customerName={customer.name}
              accountNumber={customer.accountNumber}
            />
          </TabsContent>

          {/* Disputes Tab */}
          <TabsContent value="disputes">
            <DisputesTab 
              customerId={customer.id} 
              customerName={customer.name}
            />
          </TabsContent>

          {/* Communications Tab */}
          <TabsContent value="communications">
            <CommunicationHistoryTab customerId={customer.id} customerName={customer.name} />
          </TabsContent>

          {/* Contacts Tab */}
          <TabsContent value="contacts">
            <AllContactsTab customerId={customer.id} customerName={customer.name} />
          </TabsContent>

          {/* Addresses Tab */}
          <TabsContent value="addresses">
            <AddressesTab 
              customerId={customer.id} 
              customerName={customer.name}
              primaryAddress={customer.address}
            />
          </TabsContent>

          {/* Notes Tab */}
          <TabsContent value="notes">
            <NotesTab customerId={customer.id} customerName={customer.name} />
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents">
            <DocumentsTab customerId={customer.id} customerName={customer.name} />
          </TabsContent>
        </Tabs>

        {/* Transaction Detail Sheet */}
        <TransactionDetailSheet
          open={detailSheetOpen}
          onOpenChange={setDetailSheetOpen}
          transaction={selectedTransaction}
          onViewDocument={(transaction) => {
            if (transaction.documentUrl) {
              window.open(transaction.documentUrl, "_blank");
            }
          }}
        />

        {/* Selection Toolbar - Outstanding Tab */}
        {activeTransactionTab === "outstanding" && selectedRows.size > 0 && (
          <SelectionToolbar
            selectedCount={selectedRows.size}
            totals={selectionTotals}
            selectedTransactions={selectedTransactions}
            onClearSelection={handleClearSelection}
            onExportSelected={handleExportSelected}
            onSendReminders={() => setSendRemindersDialogOpen(true)}
            onAllocateReceipt={handleNavigateToAllocate}
            onSendCopyInvoice={() => setSendCopyInvoiceDialogOpen(true)}
            onCreateCollectionCase={() => setCreateCollectionCaseModalOpen(true)}
            onCreatePromise={() => setCreatePromiseModalOpen(true)}
            onCreateDispute={() => setCreateDisputeModalOpen(true)}
            onAddNote={() => setAddNoteModalOpen(true)}
          />
        )}

        {/* Selection Toolbar - History Tab */}
        {activeTransactionTab === "payment-history" && historySelectedRows.size > 0 && (
          <SelectionToolbar
            selectedCount={historySelectedRows.size}
            totals={historySelectionTotals}
            selectedTransactions={historySelectedTransactions}
            onClearSelection={handleHistoryClearSelection}
            onExportSelected={handleHistoryExportSelected}
            onSendReminders={() => setHistorySendRemindersDialogOpen(true)}
            onAllocateReceipt={handleHistoryNavigateToAllocate}
            onSendCopyInvoice={() => setHistorySendCopyInvoiceDialogOpen(true)}
            onCreateCollectionCase={() => setHistoryCreateCollectionCaseModalOpen(true)}
            onCreatePromise={() => setHistoryCreatePromiseModalOpen(true)}
            onCreateDispute={() => setHistoryCreateDisputeModalOpen(true)}
            onAddNote={() => setHistoryAddNoteModalOpen(true)}
          />
        )}

        {/* Send Reminders Dialog - Outstanding Tab */}
        <SendRemindersDialog
          open={sendRemindersDialogOpen}
          onOpenChange={setSendRemindersDialogOpen}
          transactions={selectedTransactions}
          onSend={handleSendReminders}
        />

        {/* Allocate Receipt Dialog - Outstanding Tab */}
        <AllocateReceiptDialog
          open={allocateReceiptDialogOpen}
          onOpenChange={setAllocateReceiptDialogOpen}
          receipts={selectedTransactions}
          availableInvoices={outstandingTransactions}
          onAllocate={handleAllocateReceipt}
        />

        {/* Send Reminders Dialog - History Tab */}
        <SendRemindersDialog
          open={historySendRemindersDialogOpen}
          onOpenChange={setHistorySendRemindersDialogOpen}
          transactions={historySelectedTransactions}
          onSend={handleHistorySendReminders}
        />

        {/* Allocate Receipt Dialog - History Tab */}
        <AllocateReceiptDialog
          open={historyAllocateReceiptDialogOpen}
          onOpenChange={setHistoryAllocateReceiptDialogOpen}
          receipts={historySelectedTransactions}
          availableInvoices={completedTransactions}
          onAllocate={handleHistoryAllocateReceipt}
        />

        {/* Send Copy Invoice Dialog - Outstanding Tab */}
        <SendCopyInvoiceDialog
          open={sendCopyInvoiceDialogOpen}
          onOpenChange={setSendCopyInvoiceDialogOpen}
          transactions={selectedTransactions}
          onSend={handleSendCopyInvoice}
        />

        {/* Create Collection Case Drawer - Outstanding Tab */}
        <CreateCaseDrawer
          open={createCollectionCaseModalOpen}
          onOpenChange={setCreateCollectionCaseModalOpen}
          onCaseCreated={handleCreateCollectionCase}
        />

        {/* Create Promise to Pay Drawer - Outstanding Tab */}
        <CreatePromiseDrawer
          open={createPromiseModalOpen}
          onOpenChange={setCreatePromiseModalOpen}
          prePopulatedCustomer={
            selectedTransactions.length > 0 && selectedTransactions[0]
              ? {
                  customerId: selectedTransactions[0].customerId.toString(),
                  customerName: selectedTransactions[0].customerName || '',
                  accountNumber: `ACC-${selectedTransactions[0].customerId.toString().padStart(3, '0')}`,
                }
              : undefined
          }
          onSave={handleCreatePromise}
        />

        {/* Create Dispute Modal - Outstanding Tab */}
        <CreateDisputeModal
          open={createDisputeModalOpen}
          onOpenChange={setCreateDisputeModalOpen}
          selectedTransactions={selectedTransactions}
          onSave={handleCreateDispute}
        />

        {/* Add Note Modal - Outstanding Tab */}
        <AddNoteModal
          open={addNoteModalOpen}
          onOpenChange={setAddNoteModalOpen}
          selectedTransactions={selectedTransactions}
          onSave={handleAddNote}
        />

        {/* Send Copy Invoice Dialog - History Tab */}
        <SendCopyInvoiceDialog
          open={historySendCopyInvoiceDialogOpen}
          onOpenChange={setHistorySendCopyInvoiceDialogOpen}
          transactions={historySelectedTransactions}
          onSend={handleHistorySendCopyInvoice}
        />

        {/* Create Collection Case Drawer - History Tab */}
        <CreateCaseDrawer
          open={historyCreateCollectionCaseModalOpen}
          onOpenChange={setHistoryCreateCollectionCaseModalOpen}
          onCaseCreated={handleHistoryCreateCollectionCase}
        />

        {/* Create Promise to Pay Drawer - History Tab */}
        <CreatePromiseDrawer
          open={historyCreatePromiseModalOpen}
          onOpenChange={setHistoryCreatePromiseModalOpen}
          prePopulatedCustomer={
            historySelectedTransactions.length > 0 && historySelectedTransactions[0]
              ? {
                  customerId: historySelectedTransactions[0].customerId.toString(),
                  customerName: historySelectedTransactions[0].customerName || '',
                  accountNumber: `ACC-${historySelectedTransactions[0].customerId.toString().padStart(3, '0')}`,
                }
              : undefined
          }
          onSave={handleHistoryCreatePromise}
        />

        {/* Create Dispute Modal - History Tab */}
        <CreateDisputeModal
          open={historyCreateDisputeModalOpen}
          onOpenChange={setHistoryCreateDisputeModalOpen}
          selectedTransactions={historySelectedTransactions}
          onSave={handleHistoryCreateDispute}
        />

        {/* Add Note Modal - History Tab */}
        <AddNoteModal
          open={historyAddNoteModalOpen}
          onOpenChange={setHistoryAddNoteModalOpen}
          selectedTransactions={historySelectedTransactions}
          onSave={handleHistoryAddNote}
        />
      </div>
    );
  } catch (error) {
    console.error("Error rendering customer detail page:", error);
    return (
      <div className="flex flex-col gap-6 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <h2 className="text-2xl font-semibold text-red-600 mb-2">Rendering Error</h2>
            <p className="text-muted-foreground mb-4">
              {error instanceof Error ? error.message : "An unknown error occurred while rendering the page"}
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              Customer: {customer?.name || "Unknown"} (ID: {customerId})
            </p>
            <Button onClick={() => navigate("/customers")}>
              Back to Customers
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
